import ContactProfileEditor from 'components/ContactProfileEditor'
import React, { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, Box, Button, Card, CardContent, CardHeader, Grid, Stack, Table, TableBody, TableContainer, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { Loader } from 'components'
import BusinessPage from 'modules/Business/components/BusinessPage'
import InternalHeader from 'components/Header/InternalHeader'
import AddressFormFields from 'components/address/AddressFormFields'
import Definition from 'modules/Business/modules/Info/components/Definition'
import { fetchBusinesses, fetchUser } from 'modules/User/actions'
import { updateAccountName } from 'modules/Business/actions'
import { currentPageUrl, loginUrl } from 'utils/loginRedirect'
import api from 'utils/api'

const fields = ['name', 'street', 'addressLine2', 'city', 'state', 'zipCode', 'countryCode', 'countryName']

export default function BusinessAccount({ embedded = false }) {
  const { organizationId } = useParams()
  const location = useLocation()
  const dispatch = useDispatch()
  const user = useSelector(state => state.user)
  const userId = user.details?.userId
  const { enqueueSnackbar } = useSnackbar()
  const [account, setAccount] = useState(null)
  const [form, setForm] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(false)
  const pending = useRef(false)
  const activeId = useRef(organizationId)
  activeId.current = organizationId

  useEffect(() => {
    if (!userId) return
    let current = true
    setAccount(null)
    setEditing(false)
    setError('')
    api.get(`/business-accounts/${organizationId}`).then(data => {
      if (!current) return
      setAccount(data)
      setForm(Object.fromEntries(fields.map(field => [field, data[field] || ''])))
    }).catch(() => { if (current) setError('This business account could not be loaded. You may not have access.') })
    return () => { current = false }
  }, [organizationId, userId])

  if (user.isFetching && !user.details) return <Loader />
  if (!user.details) return <Navigate to={loginUrl(currentPageUrl(location))} replace />
  const isOwner = String(account?.ownerUserId) === String(userId)
  const canEdit = isOwner && account?.canEdit && user.details.lifecycleStatus !== 'SUSPENDED'
  const save = async event => {
    event.preventDefault()
    if (!canEdit || pending.current) return
    pending.current = true
    setSaving(true)
    const requestedId = organizationId
    try {
      const saved = await api.put(`/business-accounts/${organizationId}`, Object.fromEntries(fields.map(field => [field, form[field]])))
      if (activeId.current !== requestedId) return
      setAccount(saved)
      setForm(Object.fromEntries(fields.map(field => [field, saved[field] || ''])))
      setEditing(false)
      dispatch(updateAccountName(saved.organizationId, saved.name))
      dispatch(fetchUser())
      api.get('/user/businesses').then(result => dispatch(fetchBusinesses(result.content)))
        .catch(() => enqueueSnackbar('Account saved. Refresh the page to update the business list.', { variant: 'warning' }))
      enqueueSnackbar('Business account saved.', { variant: 'success' })
    } catch (failure) {
      if (activeId.current === requestedId) enqueueSnackbar(failure.message || 'Business account could not be saved.', { variant: 'error' })
    } finally {
      pending.current = false
      setSaving(false)
    }
  }

  const cancelEditing = () => {
    setForm(Object.fromEntries(fields.map(field => [field, account[field] || ''])))
    setEditing(false)
  }

  const content = <Box p={2}>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        {!embedded && <Button component={Link} to="/business">Back to my business</Button>}
        {isOwner && !editing && <Button variant="contained" disabled={!canEdit} onClick={() => setEditing(true)}>Edit details</Button>}
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {!account && !error && <Loader />}
      {account && <Grid container spacing={2}>
        <Grid item xs={12} md={8} lg={6}>
          {!canEdit && <Alert severity="info" sx={{ mb: 2 }}>This account is read-only. Only its active owner can edit account information.</Alert>}
          <Card>
            <CardHeader subheader="Account details" />
            <CardContent>
              {isOwner && editing ? <Box component="form" onSubmit={save}>
                <Box component="fieldset" disabled={saving || !canEdit} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
                  <TextField fullWidth required label="Account name" value={form.name || ''} inputProps={{ maxLength: 255 }}
                    onChange={event => setForm(current => ({ ...current, name: event.target.value }))} sx={{ mb: 2 }} />
                  <AddressFormFields values={form} onChange={changes => setForm(current => ({ ...current, ...changes }))} />
                  <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                    <Button type="submit" variant="contained" disabled={saving || !canEdit}>{saving ? 'Saving' : 'Save changes'}</Button>
                    <Button onClick={cancelEditing} disabled={saving}>Cancel</Button>
                  </Stack>
                </Box>
              </Box> : <TableContainer>
                <Table aria-label="Account details">
                  <TableBody>
                    <Definition id="Account" value={account.name} />
                    <Definition id="Street" value={account.street || '—'} />
                    {account.addressLine2 && <Definition id="Suite / apt" value={account.addressLine2} />}
                    <Definition id="City" value={account.city || '—'} />
                    <Definition id="State / region" value={account.state || '—'} />
                    <Definition id="Postal code" value={account.zipCode || '—'} />
                    <Definition id="Country" value={account.countryName || account.countryCode || '—'} />
                  </TableBody>
                </Table>
              </TableContainer>}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Account information is separate from each business profile. Changes here do not update business names or addresses.</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4} lg={6}>
          <Card>
            <CardHeader subheader="Account owner" />
            <CardContent>
              {editing && isOwner ? <Card variant="outlined" sx={{ p: 2, borderColor: 'primary.main' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Account owner</Typography>
                <ContactProfileEditor key={organizationId} contact={account.owner} editable={isOwner} disabled={!canEdit || saving}
                  onSaved={profile => setAccount(current => current?.organizationId === account.organizationId
                    ? { ...current, owner: { ...current.owner, ...profile } } : current)} />
              </Card> : <TableContainer>
                <Table aria-label="Account owner">
                  <TableBody>
                    <Definition id="First name" value={account.owner?.firstName || 'Not provided'} />
                    <Definition id="Last name" value={account.owner?.lastName || 'Not provided'} />
                    <Definition id="Email" value={account.owner?.email || 'Not provided'} />
                    <Definition id="Phone" value={account.owner?.phone || 'Not provided'} />
                  </TableBody>
                </Table>
              </TableContainer>}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {isOwner ? 'Your profile supplies the name, email, and phone shown here.' : "The account owner's profile supplies the name, email, and phone shown here."}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {isOwner ? 'You own this account. Only you can edit account details.' : 'Only this owner can edit account details.'}
                {' '}Business contacts can access assigned businesses, but cannot edit this account.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>}
    </Box>

  return embedded ? <BusinessPage title="Business account" showReadOnlyNotice={false}>{content}</BusinessPage> :
    <Box component="main" sx={{ backgroundColor: '#fcfcfc', minHeight: '100vh' }}>
      <InternalHeader title="Business account" />
      {content}
    </Box>
}
